import {
  HttpClient,
  HttpContext,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BFF_ERROR_CODES } from '@core/constants/bff-error-messages';
import { logHttpContext } from '@core/constants/log-http-context';
import { LogEvent } from '@core/enums/log-event';
import { LoggerService } from '@core/services/logger.service';
import {
  Observable,
  catchError,
  filter,
  firstValueFrom,
  map,
  mergeMap,
  of,
  startWith,
  switchMap,
  take,
  tap,
  throwError,
  timeout,
} from 'rxjs';
import {generateRequestHeader, generateRequestTimeLogHeader} from 'src/app/core/interceptors/log.interceptor';
import { StartFilters } from 'src/app/core/interfaces/start';
import { UserService } from 'src/app/core/services/user.service';
import { PhoneNumberHelper } from 'src/app/shared/helpers/phone-parser.helper';
import { environment } from 'src/environments/environment';
import { BroadcastMediatorService } from '../../../core/services/broadcast-mediator.service';
import { CompanyNotProvidedException } from '../../../shared/exceptions/company-not-provided.exception';
import {
  BookingAttendant,
  ExpediaAttendant,
  FacebookAutomationAttendant,
  GoogleAutomationAttendant,
  NotAssignedAttendant,
  WhatsAppWebAttendant,
} from '../constants/attendant.constant';

import { attendanceStatusToArray } from '../helpers/attendance-status-to-array.helper';
import { Attendance, ContactChannel } from '../interfaces/attendance';
import { ChannelUtilsService } from './channel-utils.service';
import { AttendanceContactParams } from '../interfaces/attendance-contact.interface';
import { AttendanceContactHistory } from '../interfaces/attendance-list-contact-history.interface';
import {
  AttendanceContentType,
  AttendanceHistoryResponse,
  AttendanceListHistoryParams,
  AttendanceListOldestHistoryParams,
  AttendanceMessage,
} from '../interfaces/attendance-list-history.interface';
import {
  AttendanceListRequest,
  PaginatedAttendances,
} from '../interfaces/attendance-list.interface';
import { AttendanceStatus } from '../interfaces/attendance-status.enum';
import { TotalCredits } from '../interfaces/credits-management.interface';
import {
  MediaDetail,
  MediaPayload,
  PreSignedUrl,
  SendSignedUpload,
  UploadByPreSigned,
} from '../interfaces/media-payload.interface';
import {
  WhatsappInvitation,
  WhatsappInvitationPayload,
} from '../interfaces/whatsapp-invitation.interface';
import { RequestContactInfoPayload } from '../interfaces/request-contact-info.interface';
import { ChannelMessageTextParserService } from './channel-message-text-parser.service';
import { LiveChatSendMessagePreviewService } from './live-chat-send-message-preview.service';
import { LiveChatStateService } from './live-chat-state.service';
import { IntentTrack } from '../interfaces/intent-track.interface';
import { AttendanceHistoryJson } from '../interfaces/attendance-history-json.interface';
import { Tag, TagWithCompanyInfo } from '../interfaces/tag';

const AUTOMATION_AGENT_NAME_MAP: Record<string, string> = {
  [WhatsAppWebAttendant]: 'WhatsApp Web',
  [BookingAttendant]: 'Booking',
  [ExpediaAttendant]: 'Expedia',
  [FacebookAutomationAttendant]: 'Facebook',
  [GoogleAutomationAttendant]: 'Google Business Messages',
};

const AUTOMATION_AGENT_IDS = Object.keys(AUTOMATION_AGENT_NAME_MAP);

export type MarkAsResolvedRequestParams = {
  attendanceId: string;
  channelReferenceId: string;
  changeTo: AttendanceStatus;
  shouldReconnectBot: boolean;
  companiesIds: string[];
};

export type UpdateAttendanceTagRequest = {
  attendanceId: string;
  companyId: string;
  channelReferenceId: string;
  tagIds: string[];
};

@Injectable({
  providedIn: 'root',
})
export class AttendanceService {
  private _lastHistoryCreatedAt = '';

  constructor(
    private httpClient: HttpClient,
    private liveChatState: LiveChatStateService,
    private userService: UserService,
    private parsePhoneNumber: PhoneNumberHelper,
    private liveChatSendMessagePreviewService: LiveChatSendMessagePreviewService,
    private messageParserService: ChannelMessageTextParserService,
    private logger: LoggerService,
    private channelUtilsService: ChannelUtilsService,
  )
  {}

  /**
   * This gets a list of attendances based on the filters passed in.
   * However, the http method of the request to the back-end must be a POST
   * because of the URL size limit.
   * @param AttendanceListRequest
   * @returns
   */
  get(attendanceListRequest: AttendanceListRequest) {
    let {
      attendanceStatus,
      dateRange,
      companyIds,
      searchInput,
      attendantIds,
      channelIds,
      tagIds,
      messageStatus,
      attendanceResponse,
      publicId,
      assignedAttendant,
      lastAttendanceChain,
      freeEntryPoint,
    } = attendanceListRequest.filterState;

    const {
      attendantIds: defaultAttendantIds,
      channelIds: defaultChannelIds,
      attendanceStatus: defaultAttendanceStatus,
      tagIds: defaultTagIds,
    } = attendanceListRequest.defaultFilterState;

    const { page, itemsPerPage } =
      attendanceListRequest.attendanceListPagination;

    if (!companyIds?.length) {
      return throwError(
        () =>
          new CompanyNotProvidedException(
            'At least one Company must be provided'
          )
      );
    }

    const userId = this.userService.getCurrentUser().id;
    const defaultAttendanceStatusArr = attendanceStatusToArray(
      defaultAttendanceStatus
    );
    let attendanceStatusArr = attendanceStatusToArray(attendanceStatus);

    tagIds = defaultTagIds.length === tagIds.length ? [] : tagIds;
    channelIds =
      defaultChannelIds.length === channelIds.length ? [] : channelIds;
    attendantIds =
      defaultAttendantIds.length === attendantIds.length ? [] : attendantIds;
    attendanceStatusArr =
      defaultAttendanceStatusArr.length === attendanceStatusArr.length
        ? []
        : attendanceStatusArr;

    searchInput = this.parsePhoneNumber.parse(searchInput);

    const notAssigned = assignedAttendant
      ? false
      : attendantIds?.includes(NotAssignedAttendant);
    const automationAgentNames = assignedAttendant
      ? ''
      : attendantIds
          ?.filter((id) => AUTOMATION_AGENT_IDS.includes(id))
          .map((id) => AUTOMATION_AGENT_NAME_MAP[id])
          .join(',');
    const attendantsIds = assignedAttendant
      ? ''
      : attendantIds
          ?.filter((id) => id !== NotAssignedAttendant && !AUTOMATION_AGENT_IDS.includes(id))
          ?.join(',');

    const params: Record<string, any> = {
      userId: userId.toString(),
      companiesIds: companyIds.join(','),
      startDate: dateRange.from,
      ...((dateRange.label === 'CUSTOM_PERIOD' ||
        dateRange.label === 'YESTERDAY') && { endDate: dateRange.to }),
      status: attendanceStatusArr.toString(),
      page: page.toString(),
      channels: channelIds.join(','),
      itemsPerPage: itemsPerPage.toString(),
      ...(publicId && { publicId: publicId }),
      search: searchInput,
      tags: tagIds.join(','),
      waitingForReply: attendanceResponse === 'pending',
      withReadMessages: messageStatus.read,
      withUnreadMessages: messageStatus.unread,
      notAssigned,
      automationAgentNames,
      attendantsIds,
      assignedAttendant,
      lastAttendanceChain,
      ...(freeEntryPoint && { freeEntryPoint }),
      ...(attendanceListRequest.keepAttendanceSelectedId && {
        keepAttendanceSelectedId: true,
      }),
    };

    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(
        ([_, value]) => value != null && value !== ''
      )
    );
    const contextHttp = new HttpContext().set(logHttpContext, {
      date: dateRange.label?.toLocaleLowerCase(),
    });

    return this.httpClient.post<PaginatedAttendances>(
      `${environment.externalUrls.bff}/attendance/list`,
      filteredParams,
      {
        headers: generateRequestTimeLogHeader('attendance-list'),
        context: contextHttp,
      }
    );
  }

  updateAttendanceTags(
    payload: UpdateAttendanceTagRequest
  ): Observable<void> {
    return this.httpClient.post<void>(
      `${environment.externalUrls.bff}/attendance/tag`,
      payload,
      {
        headers: generateRequestTimeLogHeader('attendance-tag'),
      }
    );
  }

  getHistory(
    params: AttendanceListHistoryParams
  ): Observable<AttendanceHistoryResponse> {
    const _params = new HttpParams({
      fromObject: {
        attendanceId: params.attendanceId,
        ...(params.previousAttendanceId && {
          previousAttendanceId: params.previousAttendanceId,
        }),
        itemsPerPage: params.itemsPerPage,
        page: params.page,
        companiesIds: [params.companyId],
        channelReferenceId: params.channelReferenceId,
        ...(params.cachedHasUnreadMessages !== undefined && {
          cachedHasUnreadMessages: params.cachedHasUnreadMessages,
        }),
        ...(params.cachedLastContactEventAt && {
          cachedLastContactEventAt: params.cachedLastContactEventAt,
        }),
      },
    });

    const request$ = this.httpClient
      .get<AttendanceHistoryResponse>(
        `${environment.externalUrls.bff}/attendance/history`,
        {
          params: _params,
          headers: generateRequestTimeLogHeader('attendance-history'),
        }
      )
      .pipe(
        tap(({ attendanceHistory }) => {
          const historyLength = attendanceHistory.length;
          if (historyLength) {
            this._lastHistoryCreatedAt =
              attendanceHistory[historyLength - 1].createdAt;
          }
        }),
        mergeMap((history) => {
          const { shouldCheckForOldestHistory } = history;
          if (
            !shouldCheckForOldestHistory ||
            !params?.channel ||
            !params?.channelReferenceId
          ) {
            return of(history);
          }

          return this.getOldestHistory({
            channel: params.channel,
            lastCreatedAt: this._lastHistoryCreatedAt,
            channelReferenceId: params.channelReferenceId,
            companyId: params.companyId,
          }).pipe(
            map((oldestEvent) => ({
              ...history,
              attendanceHistory: oldestEvent ? [oldestEvent] : [],
            })),
            startWith(history)
          );
        })
      );

    return this.waitForSocketConnection().pipe(switchMap(() => request$));
  }

  private waitForSocketConnection(timeoutMs = 10000): Observable<unknown> {
    if (this.liveChatState.currentSocketConnectionId) {
      return of(true);
    }

    return this.liveChatState.socketConnectionId$.pipe(
      filter((connectionId) => !!connectionId),
      take(1),
      timeout({ first: timeoutMs, with: () => of(undefined) })
    );
  }

  getHistoryByEvent(params: any): Observable<AttendanceHistoryResponse> {
    const _params = new HttpParams({
      fromObject: {
        ...params,
      },
    });

    return this.httpClient.get<AttendanceHistoryResponse>(
      `${environment.externalUrls.bff}/attendance/history/event`,
      {
        params: _params,
        headers: generateRequestTimeLogHeader('attendance-history-by-event'),
      }
    );
  }

  public assumeAttendance() {
    if (
      this.userService.getCurrentUser().id ===
      Number(
        this.liveChatState.currentAttendance?.currentAttendant?.userReferenceId
      )
    ) {
      return this.setAttendanceStatus(AttendanceStatus.OPENED, false);
    }
    return this.toggleRobotState(false);
  }

  public assumeAttendanceFor(attendance: Attendance) {
    const currentUser = this.userService.getCurrentUser();
    if (currentUser.id === Number(attendance.currentAttendant?.userReferenceId)) {
      return this.setAttendanceStatus(AttendanceStatus.OPENED, false, attendance);
    }
    return this.toggleRobotState(false, attendance);
  }

  openAttendance() {
    return this.setAttendanceStatus(AttendanceStatus.OPENED, false);
  }

  closeAttendance() {
    return this.setAttendanceStatus(AttendanceStatus.CLOSED, true);
  }

  setAttendanceStatus(changeTo: AttendanceStatus, shouldReconnectBot: boolean, attendanceOverride?: Attendance) {
    const attendance = attendanceOverride ?? this.liveChatState.currentAttendance;

    if (
      !attendance?.id ||
      !attendance?.currentContactChannel?.channelReferenceId
    ) {
      return throwError(
        () => new Error('Attendance id and channelReferenceId must be provided')
      );
    }

    if (attendance.status === AttendanceStatus.ARCHIVED) {
      return throwError(
        () => ({ code: BFF_ERROR_CODES.ATTENDANCE_ALREADY_ARCHIVED })
      );
    }

    if (attendance.status === changeTo) {
      return throwError(
        () => new Error(`Cannot change attendance status from ${attendance.status} to ${changeTo}`)
      );
    }

    const params = {
      attendanceId: attendance.id,
      channelReferenceId: attendance.currentContactChannel.channelReferenceId,
      changeTo,
      shouldReconnectBot,
      companiesIds: [attendance.currentCompany.id],
    } as MarkAsResolvedRequestParams;

    return this.httpClient.put(
      `${environment.externalUrls.bff}/attendance/status`,
      params,
      { headers: generateRequestTimeLogHeader('attendance-status') }
    );
  }

  /**
   * Send a text message to the backend to process and add it to the chat to improve user experience.
   * We add an Id to track the message and check if it was successfully sent.
   * Messages that are sent through this method should appear in the screen before the request completes.
   * The Backend is set to send this message through websocket to the frontend again with the status, so we can update it.
   * @param text
   * @param attendanceMessage
   */
  sendMessage(text: string, attendanceMessage?: AttendanceMessage) {
    const attendance = this.liveChatState.currentAttendance!;

    if (!attendance?.currentContactChannel?.channelReferenceId) {
      return throwError(() => new Error('channelReferenceId must be provided'));
    }

    const frontendMessageId =
      this.liveChatSendMessagePreviewService.previewSendMessage(
        text,
        this.liveChatState.currentAttendance?.currentContactChannel,
        attendanceMessage
      );
    text = this.messageParserService.adaptOutgoingMessages(text);

    const body = {
      text,
      channelReferenceId: attendance.currentContactChannel.channelReferenceId,
      //TODO: verificar se esse frontendMessageId não deveria ser gerado no backend e retornado como responde
      frontendMessageId,
      companiesIds: [attendance.currentCompany.id],
    };

    const isChannelWithReply = this.channelUtilsService.isChannelWithReply(attendance.currentContactChannel.channel);
    if (isChannelWithReply && attendanceMessage?.data?.content?.platformReferenceId) {
      Object.assign(body, {
        replyData: {
          referenceId: attendanceMessage.data.content.platformReferenceId,
        },
      });
    }

    return this.httpClient.post(
      `${environment.externalUrls.bff}/send/text`,
      body,
      { headers: generateRequestHeader('send-text', attendance?.currentContactChannel?.channel || null) }
    );
  }

  sendMedia(mediaPayload: MediaPayload) {
    if (!mediaPayload.channelReferenceId) {
      return throwError(() => new Error('channelReferenceId must be provided'));
    }

    let sendUploadFinished: SendSignedUpload;
    const frontendMessageId =
      this.liveChatSendMessagePreviewService.previewSendMessage(mediaPayload);

    const mediaDetail = this.generateFileDetail(mediaPayload);

    this.logger.log(LogEvent.LIVECHAT_MEDIA_PRE_SIGNED_URL_START, {
      channelReferenceId: mediaPayload.channelReferenceId,
      frontendMessageId,
      fileName: mediaDetail.originalName,
      contentType: mediaDetail.contentType,
      extension: mediaDetail.extension,
    });

    const preSignedUrl$ = this.generatePreSignedUrl(mediaDetail);

    const uploadByPreSigned$ = preSignedUrl$.pipe(
      tap((preSignedUrl) => {
        this.logger.log(LogEvent.LIVECHAT_MEDIA_PRE_SIGNED_URL_SUCCESS, {
          channelReferenceId: mediaPayload.channelReferenceId,
          frontendMessageId,
          fileName: mediaDetail.originalName,
          objectFile: preSignedUrl?.objectFile,
        });
      }),
      catchError((error) => {
        this.logger.log(LogEvent.LIVECHAT_MEDIA_PRE_SIGNED_URL_ERROR, {
          channelReferenceId: mediaPayload.channelReferenceId,
          frontendMessageId,
          fileName: mediaDetail.originalName,
          contentType: mediaDetail.contentType,
          extension: mediaDetail.extension,
          error,
        });
        return throwError(() => error);
      }),
      map(
        (preSignedUrl): UploadByPreSigned =>
          Object.assign(mediaDetail, preSignedUrl)
      ),
      tap((uploadPreSignedUrl) => {
        sendUploadFinished = Object.assign(uploadPreSignedUrl, {
          frontendMessageId,
          companiesIds: mediaPayload.companiesIds,
        });
      }),
      mergeMap((uploadByPreSigned) =>
        this.uploadByPreSignedUrl(uploadByPreSigned).pipe(
          catchError((error) => {
            this.logger.log(LogEvent.LIVECHAT_MEDIA_UPLOAD_ERROR, {
              uploadByPreSigned,
              error,
            });
            return throwError(() => error);
          })
        )
      )
    );

    return uploadByPreSigned$.pipe(
      mergeMap(() => this.sendUploadFinished(sendUploadFinished)),

      catchError((error) => {
        frontendMessageId &&
          this.liveChatSendMessagePreviewService.failedSendMessage(
            frontendMessageId
          );
        return throwError(() => error);
      })
    );
  }

  sendWhatsappInvitation(whatsappInvitation: WhatsappInvitation) {
    const frontendMessageId =
      this.liveChatSendMessagePreviewService.previewSendMessage({
        type: AttendanceContentType.LIST_BUTTONS,
        whatsappInvitation,
      } as WhatsappInvitationPayload);

    return this.httpClient.post(
      `${environment.externalUrls.bff}/send/whatsapp/invite`,
      {
        frontendMessageId,
        ...whatsappInvitation,
      },
      { headers: generateRequestTimeLogHeader('send-whatsapp-invitation') }
    );
  }

  /**
   * TAS-572: asks the WhatsApp traveler to share their phone number
   * (request_contact_info). Tracked by the bot as a text, so the optimistic
   * preview is the body text.
   */
  requestContactInfo(text: string) {
    const attendance = this.liveChatState.currentAttendance;

    if (!attendance?.currentContactChannel?.channelReferenceId) {
      return throwError(() => new Error('channelReferenceId must be provided'));
    }

    const frontendMessageId =
      this.liveChatSendMessagePreviewService.previewSendMessage(
        text,
        attendance.currentContactChannel
      );

    const body: RequestContactInfoPayload = {
      attendanceId: attendance.id,
      channelReferenceId: attendance.currentContactChannel.channelReferenceId,
      frontendMessageId,
      text,
      companiesIds: [attendance.currentCompany.id],
    };

    return this.httpClient
      .post(
        `${environment.externalUrls.bff}/send/whatsapp/request-contact-info`,
        body,
        { headers: generateRequestTimeLogHeader('request-contact-info') }
      )
      .pipe(
        catchError((error) => {
          frontendMessageId &&
            this.liveChatSendMessagePreviewService.failedSendMessage(
              frontendMessageId
            );
          return throwError(() => error);
        })
      );
  }

  private getOldestHistory(
    params: AttendanceListOldestHistoryParams
  ): Observable<AttendanceMessage | null> {
    const _params = new HttpParams({
      fromObject: {
        lastCreatedAt: params.lastCreatedAt,
        channel: params.channel,
        companiesIds: [params.companyId],
        channelReferenceId: params.channelReferenceId,
      },
    });
    return this.httpClient.get<AttendanceMessage | null>(
      `${environment.externalUrls.bff}/attendance/history/oldest`,
      {
        params: _params,
      }
    );
  }

  private generateFileDetail(mediaPayload: MediaPayload): MediaDetail {
    const {
      file,
      filename: originalName,
      caption,
      mediaText,
      type,
      isWhatsapp,
      channelReferenceId,
    } = mediaPayload;
    const extension = originalName.substring(originalName.lastIndexOf('.') + 1);
    const contentType = file.type;

    return {
      channelReferenceId,
      file,
      originalName,
      caption: caption || originalName,
      mediaText: mediaText
        ? this.messageParserService.adaptOutgoingMessages(mediaText)
        : mediaText,
      type,
      isWhatsapp: isWhatsapp || false,
      extension,
      contentType,
    };
  }

  private generatePreSignedUrl(mediaDetail: MediaDetail) {
    const { extension, contentType } = mediaDetail;
    return this.httpClient.get<PreSignedUrl>(
      `${environment.externalUrls.bff}/files/generate/pre-signed-url`,
      {
        params: {
          extension,
          contentType: contentType,
        },
      }
    );
  }

  private uploadByPreSignedUrl(uploadByPreSigned: UploadByPreSigned) {
    const { preSignedUrl, file, contentType } = uploadByPreSigned;
    const httpHeaders = new HttpHeaders();
    httpHeaders.append('Content-Type', contentType);
    return this.httpClient.put(preSignedUrl, file, {
      headers: httpHeaders,
    });
  }

  private sendUploadFinished(sendUploadFinished: SendSignedUpload) {
    const {
      objectFile,
      caption,
      mediaText,
      type,
      isWhatsapp,
      channelReferenceId,
      contentType,
      frontendMessageId,
      companiesIds,
      file,
    } = sendUploadFinished;
    const size = file?.size || 0;

    return this.httpClient.post(
      `${environment.externalUrls.bff}/send/media`,
      {
        fileName: objectFile,
        caption,
        mediaText,
        type,
        isWhatsapp,
        channelReferenceId: channelReferenceId,
        mimetype: contentType,
        frontendMessageId,
        companiesIds,
        size: Math.round(size / 1000000),
      },
      {
        headers: generateRequestTimeLogHeader('signal-upload-finished'),
      }
    );
  }

  toggleRobotState(activate: boolean, attendanceOverride?: Attendance) {
    const attendance = attendanceOverride ?? this.liveChatState.currentAttendance;

    if (
      !attendance?.id ||
      !attendance?.currentContactChannel?.channelReferenceId
    ) {
      return throwError(
        () => new Error('Attendance id and channelReferenceId must be provided')
      );
    }

    const endpoint = activate ? 'continue' : 'stop';

    const body = {
      attendanceId: attendance.id,
      channelReferenceId: attendance.currentContactChannel.channelReferenceId,
      companiesIds: [attendance.currentCompany.id],
    };

    return this.httpClient.post(
      `${environment.externalUrls.bff}/bot/${endpoint}`,
      body,
      { headers: generateRequestTimeLogHeader(`bot-${endpoint}`) }
    );
  }

  updateAttendanceContact(contact: AttendanceContactParams) {
    const attendance = this.liveChatState.currentAttendance;

    if (
      !attendance?.id ||
      !attendance?.currentContactChannel?.channelReferenceId ||
      !attendance?.currentCompany?.id
    ) {
      return throwError(() => new Error('Some params are missing'));
    }

    const { name, email, phone, notes } = contact;

    const body = {
      name,
      email,
      phone,
      additionalFields: {
        extra: notes,
      },
      attendanceId: attendance.id,
      channelReferenceId: attendance.currentContactChannel.channelReferenceId,
      companiesIds: [attendance.currentCompany.id],
      updatedBy: this.userService.getCurrentUser().id,
    };

    return this.httpClient.put(
      `${environment.externalUrls.bff}/attendance/contact`,
      body,
      {
        headers: generateRequestTimeLogHeader('attendance-contact'),
      }
    );
  }

  addTagsIntoAttendance(
    attendanceId: string,
    channelReferenceId: string,
    companyId: string,
    tagIds: string[]
  ): Observable<void> {
    return this.httpClient.post<void>(
      `${environment.externalUrls.bff}/attendance/tag`,
      {
        attendanceId,
        channelReferenceId,
        companyId,
        tagIds,
      },
      {
        headers: generateRequestTimeLogHeader('attendance-tag'),
      }
    );
  }

  markAsUnreadFor(attendance: Attendance) {
    return this.httpClient.patch(
      `${environment.externalUrls.bff}/attendance/${attendance.id}/mark-as-unread`,
      { companiesIds: [attendance.currentCompany.id] },
      {
        headers: generateRequestTimeLogHeader('attendance-mark-as-unread'),
      }
    );
  }

  markAsReadFor(attendance: Attendance) {
    return this.httpClient.patch<undefined>(
      `${environment.externalUrls.bff}/attendance/${attendance.id}/mark-as-read`,
      { companiesIds: [attendance.currentCompany.id] },
      {
        headers: generateRequestTimeLogHeader('attendance-mark-as-read'),
      }
    );
  }

  clearAttendanceSelectedId(attendance: Attendance) {
    return this.httpClient.patch<undefined>(
      `${environment.externalUrls.bff}/attendance/${attendance.id}/clear-selected-id`,
      { companiesIds: [attendance.currentCompany.id] },
      {
        headers: generateRequestTimeLogHeader('attendance-clear-selected-id'),
      }
    );
  }

  startFilters(companiesIds: string[]): Observable<StartFilters> {
    if (!companiesIds.length)
      return of({ tags: [], attendants: [], fixedAssignedToMe: false });

    return this.httpClient
      .get<StartFilters>(
        `${
          environment.externalUrls.bff
        }/start/filters?companiesIds=${companiesIds.join(',')}`,
        {
          headers: generateRequestTimeLogHeader('start-filters'),
        }
      )
      .pipe(
        catchError(() => {
          BroadcastMediatorService.notify('startFiltersError');
          return of({ tags: [], attendants: [], fixedAssignedToMe: false });
        })
      );
  }

  public getContactChannel(
    currentAttendance: Attendance
  ): Observable<ContactChannel[]> {
    return this.httpClient.get<ContactChannel[]>(
      `${environment.externalUrls.bff}/attendance/${currentAttendance.id}/contact-channels`,
      {
        headers: generateRequestTimeLogHeader('contact-chanel'),
        params: {
          companiesIds: [currentAttendance.currentCompany.id],
        },
      }
    );
  }

  public listContactAttendancesByAttendanceId(
    attendanceId: string,
    companyId: string
  ) {
    return this.httpClient
      .post<AttendanceContactHistory>(
        `${environment.externalUrls.bff}/attendance/list-contact-history`,
        {
          attendanceId,
        },
        {
          headers: generateRequestTimeLogHeader('list-contact-history'),
          params: {
            companiesIds: [companyId],
          },
        }
      )
      .pipe(
        map((value) => {
          //TODO: CHANGE BFF TO NOT RETURN BASE URL, BUT ONLY SEARCH PARAMS
          if (!value.available?.data.link) return value;
          try {
            //Remove default search params
            const search = new URL(value.available.data.link).search;
            value.available.data.link = `${environment.app.url}/${search}`;
            return value;
          } finally {
            return value;
          }
        })
      );
  }

  public getAttendanceDetails(attendanceId: string, companyId: string) {
    return this.httpClient.get(
      `${environment.externalUrls.bff}/attendance/${attendanceId}/find-details`,
      {
        headers: generateRequestTimeLogHeader('get-attendance-details'),
        params: {
          companiesIds: [companyId],
        },
      }
    );
  }

  public getCompanyCredits(companyId: string) {
    return this.httpClient.get<TotalCredits>(
      `${environment.externalUrls.bff}/company/credits`,
      {
        headers: generateRequestTimeLogHeader('get-company-credits'),
        params: {
          companyId,
        },
      }
    );
  }

  public transferAttendanceToUser(userId: number, enableBot = false) {
    return this.transferAttendance(userId, 'userReferenceId', enableBot);
  }

  public transferAttendanceToGroup(groupId: number, enableBot = false) {
    return this.transferAttendance(groupId, 'userGroupReferenceId', enableBot);
  }

  private transferAttendance(
    entityId: number,
    entityProp: 'userReferenceId' | 'userGroupReferenceId',
    enableBot = false
  ) {
    const attendance = this.liveChatState.currentAttendance;

    if (!attendance)
      return throwError(() => new Error('attendance is required'));

    return this.httpClient.post<void>(
      `${environment.externalUrls.bff}/attendance/assign`,
      {
        attendanceId: attendance.id,
        [entityProp]: entityId,
        enableBot,
      },
      {
        headers: generateRequestTimeLogHeader('attendance-assign'),
      }
    );
  }

  public transpictionMessage(audioUrl:string, platformReferenceId: string){
    return this.httpClient.post(
      `${environment.externalUrls.bff}/attendance/history/${platformReferenceId}/transcription`,
      {
        audioUrl,
      },
      {
        headers: generateRequestTimeLogHeader('transcription-message'),
      }
    );
  }

  public getIntentTrackById(intentTrackId: bigint){
    return this.httpClient.get<IntentTrack>(
      `${environment.externalUrls.bff}/attendance/history/intent-track/${intentTrackId}`,
      {
        headers: generateRequestTimeLogHeader('intent-track-by-id'),
      }
    );
  }

  public getAttendanceHistoryJsonById(attendanceId: string){
    return this.httpClient.get<AttendanceHistoryJson>(
      `${environment.externalUrls.bff}/attendance/${attendanceId}/history/json`,
      {
        headers: generateRequestTimeLogHeader('attendance-history-json-by-id'),
      }
    );
  }

  public getBotState(channel: string, channelReferenceId: string) {
    return this.httpClient.get(
      `${environment.externalUrls.bff}/bot/state/${channel}/${channelReferenceId}`,
      {
        headers: generateRequestTimeLogHeader('bot-state'),
      }
    );
  }

  public findTagsByIds(tagIds: string[]) {
    return this.httpClient.post<TagWithCompanyInfo[]>(
      `${environment.externalUrls.bff}/tag/find-by-ids`,
      {
        tagIds,
      },
      {
        headers: generateRequestTimeLogHeader('find-tags-by-ids'),
      }
    );
  }
}
